import { type ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

const STATUS_TONES: Record<string, string> = {
  // shared
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  OPEN: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  CANCELLED: 'bg-slate-100 text-slate-500 ring-slate-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',
  BLACKLISTED: 'bg-rose-50 text-rose-700 ring-rose-200',
  // invoices / vouchers
  CAPTURED: 'bg-sky-50 text-sky-700 ring-sky-200',
  MATCHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  VARIANCE: 'bg-amber-50 text-amber-700 ring-amber-200',
  VOUCHERED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  CERTIFIED: 'bg-sky-50 text-sky-700 ring-sky-200',
  AUTHORISED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  SUBMITTED_TREASURY: 'bg-violet-50 text-violet-700 ring-violet-200',
  TREASURY_VERIFIED: 'bg-violet-50 text-violet-700 ring-violet-200',
  SCHEDULED: 'bg-blue-50 text-blue-700 ring-blue-200',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // commitments
  REQUESTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  FUNDS_RESERVED: 'bg-blue-50 text-blue-700 ring-blue-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  REJECTED_NO_FUNDS: 'bg-rose-50 text-rose-700 ring-rose-200',
  LIQUIDATED: 'bg-slate-100 text-slate-600 ring-slate-200',
  // batches
  ASSEMBLED: 'bg-sky-50 text-sky-700 ring-sky-200',
  APPROVED_1: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  APPROVED_2: 'bg-violet-50 text-violet-700 ring-violet-200',
  RELEASED: 'bg-blue-50 text-blue-700 ring-blue-200',
  TRANSMITTED: 'bg-blue-50 text-blue-700 ring-blue-200',
  ACKNOWLEDGED: 'bg-teal-50 text-teal-700 ring-teal-200',
  SETTLED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-200',
  RETURNED: 'bg-rose-50 text-rose-700 ring-rose-200',
  // F02 budget prep (cycle / submission / initiative)
  CIRCULAR_ISSUED: 'bg-sky-50 text-sky-700 ring-sky-200',
  MOF_REVIEW: 'bg-amber-50 text-amber-700 ring-amber-200',
  BILATERALS: 'bg-amber-50 text-amber-700 ring-amber-200',
  CABINET: 'bg-violet-50 text-violet-700 ring-violet-200',
  ASSEMBLY_TABLED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  ENACTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  SUBMITTED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  PROPOSED: 'bg-sky-50 text-sky-700 ring-sky-200',
  ACCEPTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // F07 revenue (investments / sources)
  PLACED: 'bg-sky-50 text-sky-700 ring-sky-200',
  MATURED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  ROLLED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  SRC: 'bg-slate-100 text-slate-600 ring-slate-200',
  OPENCLEARANCE: 'bg-slate-100 text-slate-600 ring-slate-200',
  SEYPAY: 'bg-slate-100 text-slate-600 ring-slate-200',
  OTHER: 'bg-slate-100 text-slate-600 ring-slate-200',
  // F08 virements (status / supplementary / tier)
  APPLIED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  TABLED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  MDA_INTERNAL: 'bg-slate-100 text-slate-600 ring-slate-200',
  MOF: 'bg-sky-50 text-sky-700 ring-sky-200',
  ASSEMBLY: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  // F09 payroll (post / employee / run / garnishment)
  FROZEN: 'bg-amber-50 text-amber-700 ring-amber-200',
  ABOLISHED: 'bg-slate-100 text-slate-500 ring-slate-200',
  TERMINATED: 'bg-slate-100 text-slate-500 ring-slate-200',
  CALCULATED: 'bg-sky-50 text-sky-700 ring-sky-200',
  EXCEPTIONS_CLEARED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  MDA_VERIFIED: 'bg-violet-50 text-violet-700 ring-violet-200',
  DPA_APPROVED: 'bg-blue-50 text-blue-700 ring-blue-200',
  POSTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // F10 debt (instrument / schedule / guarantee)
  FULLY_DISBURSED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  REPAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PROJECTED: 'bg-slate-100 text-slate-600 ring-slate-200',
  OVERDUE: 'bg-rose-50 text-rose-700 ring-rose-200',
  CALLED: 'bg-amber-50 text-amber-700 ring-amber-200',
  // F11 fiscal (period close / report)
  SOFT_CLOSED: 'bg-amber-50 text-amber-700 ring-amber-200',
  HARD_CLOSED: 'bg-slate-100 text-slate-600 ring-slate-200',
  PS_SIGNED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  SUBMITTED_OAG: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // F12 procurement (tender / contract)
  PUBLISHED: 'bg-sky-50 text-sky-700 ring-sky-200',
  BIDS_CLOSED: 'bg-amber-50 text-amber-700 ring-amber-200',
  EVALUATED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  NTB_AWARDED: 'bg-violet-50 text-violet-700 ring-violet-200',
  STANDSTILL: 'bg-amber-50 text-amber-700 ring-amber-200',
  CONTRACTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // F13 assets
  PENDING_CUSTODIAN: 'bg-amber-50 text-amber-700 ring-amber-200',
  IN_USE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  TRANSFER: 'bg-sky-50 text-sky-700 ring-sky-200',
  BOARD_OF_SURVEY: 'bg-violet-50 text-violet-700 ring-violet-200',
  DISPOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  // c2-seypay settlement batches derive a pseudo-status from the `reconciled`
  // boolean, same pattern CoaAdmin.tsx uses for combination `active`.
  RECONCILED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  PENDING: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}
